<template>
    <div class="relative flex w-full flex-col gap-2">
        <div class="flex gap-2">
            <div class="flex flex-1 gap-2 rounded-sm border-2 p-2">
                <div
                    class="flex-1 bg-white"
                    :class="{ 'opacity-40': totalHitsOnTarget < 1 }"
                    :style="totalHitsOnTarget >= 1 ? getGradientStyle(1) : {}"
                ></div>
                <div
                    class="flex-1 bg-white"
                    :class="{ 'opacity-40': totalHitsOnTarget < 2 }"
                    :style="totalHitsOnTarget >= 2 ? getGradientStyle(2) : {}"
                ></div>
                <div
                    class="flex-1 bg-white"
                    :class="{ 'opacity-40': totalHitsOnTarget < 3 }"
                    :style="totalHitsOnTarget >= 3 ? getGradientStyle(3) : {}"
                ></div>
            </div>
            <div class="relative">
                <div
                    class="absolute -top-5 left-1/2 -translate-x-1/2 transform rounded font-sans text-xs font-bold whitespace-nowrap text-white/80 uppercase"
                >
                    {{ $t('score_grid.single') }}
                </div>
                <div
                    class="flex h-13 w-13 cursor-pointer items-center justify-center rounded-sm bg-white px-3 text-lg font-bold text-black transition-colors hover:bg-gray-100"
                    :class="{ 'cursor-not-allowed opacity-50': !canThrowDart }"
                    @click="addPlayerTarget('S')"
                >
                    S
                </div>
            </div>
            <div class="relative">
                <div
                    class="absolute -top-5 left-1/2 -translate-x-1/2 transform rounded font-sans text-xs font-bold whitespace-nowrap text-white/80 uppercase"
                >
                    {{ $t('score_grid.double') }}
                </div>
                <div
                    class="flex h-13 w-13 cursor-pointer items-center justify-center rounded-sm bg-white px-3 text-lg font-bold text-black transition-colors hover:bg-gray-100"
                    :class="{ 'cursor-not-allowed opacity-50': !canThrowDart }"
                    @click="addPlayerTarget('D')"
                >
                    D
                </div>
            </div>
            <div class="relative">
                <div
                    class="absolute -top-5 left-1/2 -translate-x-1/2 transform rounded font-sans text-xs font-bold whitespace-nowrap text-white/80 uppercase"
                >
                    {{ $t('score_grid.triple') }}
                </div>
                <div
                    class="flex h-13 w-13 cursor-pointer items-center justify-center rounded-sm bg-white px-3 text-lg font-bold text-black transition-colors hover:bg-gray-100"
                    :class="{ 'cursor-not-allowed opacity-50': !canThrowDart }"
                    @click="addPlayerTarget('T')"
                >
                    T
                </div>
            </div>
        </div>
        <div class="flex gap-2">
            <div
                class="flex flex-1 cursor-pointer items-center justify-center rounded-sm p-1 text-center text-xl font-bold text-black uppercase transition-all select-none"
                :class="
                    isCompletingTurn
                        ? 'cursor-not-allowed bg-gray-200'
                        : 'bg-white hover:bg-gray-100'
                "
                @click="confirmDartSelections"
            >
                <div v-if="isCompletingTurn" class="flex items-center gap-2">
                    <div
                        class="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"
                    ></div>
                    <span>{{ $t('killer.completing') }}</span>
                </div>
                <span v-else>{{ $t('killer.complete_turn') }}</span>
            </div>
            <div
                class="flex h-13 w-13 cursor-not-allowed items-center justify-center rounded-sm bg-gray-200 opacity-50"
                :title="$t('killer.undo_unavailable')"
            >
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M14.2578 11.6263L11.3021 14.4766L25.332 14.4753V22.6667H22.6654V17.1419H11.3021L14.2578 19.9922L12.4062 21.9128L6.07812 15.8099L12.4062 9.70703L14.2578 11.6263Z"
                        fill="black"
                    />
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { getDarkerColor, getLighterColor } from '@/utils/colorHelpers'
import { useMatchStore } from '@/stores/match.js'

export default {
    name: 'ScoreIndicator',
    props: {
        color: {
            type: String,
            default: '#000000',
        },
        playerTarget: {
            type: Number,
            default: null,
        },
        currentPlayer: {
            type: Object,
            default: null,
        },
        currentPlayerState: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isCompletingTurn: false,
            isSubmittingDart: false,
            turnPlayerId: null,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        totalHitsOnTarget() {
            return this.currentPlayerState?.hits_on_own_target || 0
        },
        dartsThrown() {
            return this.matchStore.getCurrentDartNumber || 0
        },
        canThrowDart() {
            return this.playerTarget && this.dartsThrown < 3 && !this.isSubmittingDart
        },
        glowStyle() {
            return {
                boxShadow: `0 0 20px 4px ${this.color}66`,
            }
        },
    },
    emits: ['target-confirmed', 'target-reset'],
    methods: {
        getGradientStyle(throwNumber) {
            const endColor = getLighterColor(this.color, 40 - throwNumber * 5)
            const startColor = getDarkerColor(this.color, 10 + throwNumber * 5)

            return {
                background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
                borderRadius: '2px',
            }
        },
        async addPlayerTarget(dartType) {
            if (!this.canThrowDart || this.isSubmittingDart) return

            if (this.dartsThrown === 0) {
                this.turnPlayerId = this.currentPlayer.id
            }

            this.isSubmittingDart = true
            try {
                await this.matchStore.submitDart({
                    profile_id: this.turnPlayerId || this.currentPlayer.id,
                    dart_number: this.playerTarget,
                    dart_type: dartType,
                    dart_index: this.dartsThrown + 1,
                })

                // Auto-complete turn after 3 darts
                if (this.dartsThrown >= 2) {
                    this.completeTurn()
                }
            } catch (error) {
                console.error('Failed to submit dart:', error)
            } finally {
                this.isSubmittingDart = false
            }
        },
        async confirmDartSelections() {
            if (!this.currentPlayer || this.isCompletingTurn) return

            if (this.dartsThrown === 0) {
                this.turnPlayerId = this.currentPlayer.id
            }

            this.isCompletingTurn = true
            try {
                const profileId = this.turnPlayerId || this.currentPlayer.id

                // Submit remaining misses to complete the turn
                const remainingDarts = 3 - this.dartsThrown
                for (let i = 0; i < remainingDarts; i++) {
                    await this.matchStore.submitDart({
                        profile_id: profileId,
                        dart_number: 0,
                        dart_type: 'Miss',
                        dart_index: this.dartsThrown + i + 1,
                    })
                }

                this.completeTurn()
            } catch (error) {
                console.error('Failed to complete turn:', error)
            } finally {
                this.isCompletingTurn = false
            }
        },
        completeTurn() {
            this.turnPlayerId = null
            this.$emit('target-confirmed')
        },
    },
}
</script>
